import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../core/models/base-state/base-state';
import { getHealthSummary, setGlobalPageSize } from '../../../../core/store/actions/app.actions';
import { PatientAllergy } from '../../models/patient-allergy';
import * as allergyActions from '../actions/patient-allergy.actions';

export interface PatientAllergiesState extends EntityState<PatientAllergy> {
  baseState: baseState.BaseState;
  pageSize: number;
  isFullDatasetLoaded: boolean;
  cachePatientId: number | null;
  cacheLoadedAt: number | null;
  isFullDatasetLoading: boolean;
  fullDatasetError: unknown;
}

export const patientAllergiesAdapter = createEntityAdapter<PatientAllergy>({
  selectId: patientAllergy => patientAllergy.patientAllergyId,
});

export const initialState: PatientAllergiesState = patientAllergiesAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  pageSize: baseState.DEFAULT_PAGE_SIZE,
  isFullDatasetLoaded: false,
  cachePatientId: null,
  cacheLoadedAt: null,
  isFullDatasetLoading: false,
  fullDatasetError: null,
});

export const patientAllergiesFeatureKey = 'patient-allergies';

export const patientAllergiesReducer = createReducer(
  initialState,

  on(
    allergyActions.getPatientAllergies,
    getHealthSummary,
    (state): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
      fullDatasetError: null,
    })
  ),

  on(
    allergyActions.resetFilters,
    (state): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isActive: true,
        searchTerm: null,
        pageNumber: 1,
      },
    })
  ),

  on(
    allergyActions.getPatientAllergiesSuccess,
    (state, { patientAllergies, pageNumber, isAtEndOfData, totalRecordCount }): PatientAllergiesState => {
      const nextState =
        pageNumber <= 1
          ? patientAllergiesAdapter.setAll(patientAllergies, state)
          : patientAllergiesAdapter.addMany(patientAllergies, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(
    allergyActions.getPatientAllergiesFailure,
    (state, { error }): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      fullDatasetError: null,
    })
  ),

  on(
    allergyActions.getAllPatientAllergies,
    (state): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
      },
      isFullDatasetLoading: true,
      fullDatasetError: null,
    })
  ),

  on(
    allergyActions.getAllPatientAllergiesSuccess,
    (state, { patientAllergies, patientId, loadedAt }): PatientAllergiesState => {
      const nextState = patientAllergiesAdapter.setAll(patientAllergies, state);
      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          error: null,
          isLoading: false,
          pageNumber: 1,
          isAtEndOfData: true,
          totalRecordCount: patientAllergies.length,
        },
        isFullDatasetLoaded: true,
        cachePatientId: patientId,
        cacheLoadedAt: loadedAt,
        isFullDatasetLoading: false,
        fullDatasetError: null,
      };
    }
  ),

  on(
    allergyActions.getAllPatientAllergiesFailure,
    (state, { error }): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
      isFullDatasetLoading: false,
      fullDatasetError: error,
    })
  ),

  on(
    allergyActions.invalidateAllergyCache,
    (state): PatientAllergiesState => ({
      ...state,
      isFullDatasetLoaded: false,
      cachePatientId: null,
      cacheLoadedAt: null,
      isFullDatasetLoading: false,
      fullDatasetError: null,
    })
  ),

  on(
    allergyActions.setPageNumber,
    (state, { pageNumber }): PatientAllergiesState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber,
      },
    })
  ),

  on(
    setGlobalPageSize,
    (state, { pageSize }): PatientAllergiesState => ({
      ...state,
      pageSize,
    })
  )
);
